/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Tabs as CTabs, Spinner, TabPanel, TabPanels, useDisclosure, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { IoChevronDown } from 'react-icons/io5';
import { MdLocationPin } from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Text } from '../../components/atoms/Text';
import { PromotionGrid } from '../../components/molecules/PromotionGrid';
import { Search } from '../../components/molecules/Search';
import { useLocationContext } from '../../contexts/LocationProvider';
import { useTheme } from '../../contexts/ThemeProvider';
import { MainTemplate } from '../../templates/MainTemplate';
import useDebounce from '../../hooks/use-debounce';
import { Tabs } from '../DealsDetails/components/Tabs';
import { Categories } from './components/Categories';
import { LocationModal } from './components/LocationModal';
import { Footer } from '../../components/organisms/Footer';

const Location = () => {
  const { colors } = useTheme();
  const { forcedLocation, address, isLoading } = useLocationContext();
  const disclosure = useDisclosure();

  if (isLoading) {
    return <Spinner margin={'auto'} color={colors?.primaryColor || '#007bff'} />;
  }

  return (
    <>
      <Button
        variant={'link'}
        gap={2}
        onClick={disclosure.onOpen}
        margin={'auto'}
        width={{
          base: '100%',
          md: 'auto',
        }}
        justifyContent={'center'}
        sx={{
          '&:hover': {
            textDecoration: 'none',
          },
        }}
      >
        <MdLocationPin size="25" color="#747474" />
        <Text
          fontSize={16}
          width={{
            base: '70%',
            md: 'auto',
          }}
          overflow={'hidden'}
        >
          {forcedLocation
            ? forcedLocation?.city || forcedLocation?.state
            : address?.formattedAddress
              ? address.formattedAddress.split(' - ')[0]
              : 'Selecione sua localização'}
        </Text>
        <IoChevronDown size={25} color="#292927" />
      </Button>
      <LocationModal isOpen={disclosure?.isOpen || false} onClose={disclosure?.onClose || (() => {})} />
    </>
  );
};

export function DealsPage() {
  const router = useLocation();


  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const [selectedTab, setSelectedTab] = useState(0);
  const [search, setSearch] = useState('');
  const { latitude, longitude } = useLocationContext();

  const [tabs] = useState(['Todos', 'Cinemas', 'E-commerce', 'Lojas Físicas', 'Exclusivos']);
  const searchDebounce = useDebounce(search, 500);

  const [category, setCategory] = useState<number[]>([]);

  useEffect(() => {
    if(categoryParam) {
      setSelectedTab(tabs.findIndex((tab) => tab === categoryParam));
    }
    
    // Verificar se router.state existe antes de acessar suas propriedades
    if (router?.state?.action === 'cinema') {
      const findIndex = tabs.findIndex((tab) => tab === 'Cinemas');
      setSelectedTab(findIndex);
    } else if (router?.state?.action === 'ecommerce') {
      const findIndex = tabs.findIndex((tab) => tab === 'E-commerce');
      setSelectedTab(findIndex);
    } else if (router?.state?.action === 'local') {
      const findIndex = tabs.findIndex((tab) => tab === 'Lojas Físicas');
      setSelectedTab(findIndex);
    }
    
    if (router?.state?.mode === 'online') {
      const findIndex = tabs.findIndex((tab) => tab === 'E-commerce');
      setSelectedTab(findIndex);
    }
    
    if (router?.state?.exclusive) {
      const findIndex = tabs.findIndex((tab) => tab === 'Exclusivos');
      setSelectedTab(findIndex);
    }
  }, [router?.state, tabs, categoryParam]);

  useEffect(() => {
    // Verificar se router.state existe e é um array antes de acessar length
    if (router?.state && Array.isArray(router.state) && router.state.length > 0) {
      setCategory(router.state);
    }
  }, [router?.state]);

  return (
    <MainTemplate>
      <VStack align={'self-start'} width="100%" marginBottom={{
        base: '70px',
        md: '0px',
      }} paddingTop={'10px'} spacing={4}>
        <VStack width="100%" align={'self-start'} spacing={4} padding={'0px 20px'}>
          <Location />
        </VStack>

        <PromotionGrid
          title="Destaques"
          key="news"
          fullWidth
          params={{
            mode: 'local',
            latitude: latitude || undefined,
            longitude: longitude || undefined,
            featured: true,
          }}
        />

        <CTabs
          index={selectedTab}
          onChange={(index) => setSelectedTab(index)}
          isLazy
          borderColor="#E0E0E0"
          width="100%"
        >
          <VStack width="100%" spacing={6}>
            <VStack width="100%" align={'self-start'} spacing={4} padding={'0px 20px'}>
              <Search value={search} onChange={(t) => setSearch(t)} />
            </VStack>

            <VStack paddingLeft={'20px'} width="100%" spacing={4}>
              <Tabs tabsList={tabs} />
              <Categories selectedCategories={category} setSelectedCategories={setCategory} />
            </VStack>

            <TabPanels padding="0px 20px">
              <TabPanel key={'all'} padding={'0px'}>
                <PromotionGrid
                  key="all"
                  name="all"
                                      params={{
                      name: searchDebounce || undefined,
                      latitude: latitude || undefined,
                      longitude: longitude || undefined,
                      category_id: category?.length > 0 ? category : undefined,
                    }}
                  column
                  fullWidth
                />
              </TabPanel>
              {tabs.includes('Cinemas') && (
                <TabPanel key="cinema" padding={'0px'}>
                  <PromotionGrid
                    name="cinema"
                    key="cinema"
                    onlyBigger
                    params={{
                      category: 'Cinema',
                      name: searchDebounce || undefined,
                      latitude: latitude || undefined,
                      longitude: longitude || undefined,
                    }}
                    column
                    fullWidth
                  />
                </TabPanel>
              )}
              {tabs.includes('E-commerce') && (
                <TabPanel key={'ecommerce'} padding={'0px'}>
                  <PromotionGrid
                    name="ecommerce"
                    key={'ecommerce'}
                    params={{
                      mode: 'online',
                      category_id: category?.length > 0 ? category : undefined,
                      name: searchDebounce || undefined,
                    }}
                    column
                    fullWidth
                  />
                </TabPanel>
              )}
              {tabs.includes('Lojas Físicas') && (
                <TabPanel key={'lojas'} padding={'0px'}>
                  <PromotionGrid
                    name="lojas"
                    onlyBigger
                    key={'lojas'}
                    params={{
                      mode: 'local',
                      name: searchDebounce || undefined,
                      latitude: latitude || undefined,
                      longitude: longitude || undefined,
                      category_id: category?.length > 0 ? category : undefined,
                    }}
                    column
                    fullWidth
                  />
                </TabPanel>
              )}
              {tabs.includes('Exclusivos') && (
                <TabPanel key={'exclusivos'} padding={'0px'}>
                  <PromotionGrid
                    name="exclusivos"
                    key={'exclusivos'}
                    params={{
                      exclusive: true,
                      latitude: latitude || undefined,
                      name: searchDebounce || undefined,
                      longitude: longitude || undefined,
                      category_id: category?.length > 0 ? category : undefined,
                    }}
                    column
                    fullWidth
                  />
                </TabPanel>
              )}
              <TabPanel key={'favoritos'} padding={'0px'}>
                <PromotionGrid
                  name="favoritos"
                  key={'favoritos'}
                  params={{
                    favorite: true,
                    latitude: latitude || undefined,
                    longitude: longitude || undefined,
                    name: searchDebounce || undefined,
                  }}
                  column
                  fullWidth
                />
              </TabPanel>
            </TabPanels>
          </VStack>
        </CTabs>
        <Footer />
      </VStack>
    </MainTemplate>
  );
}
